import { useEffect, useMemo } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router';

import { ErrorBoundary, NotificationProvider } from './components';
import { HiddenColumnsProvider } from './areas/HiddenColumnsProvider';
import { createQueryClient } from './queryClient';
import { AppRoutes } from './routes';
import { getSessionState, SessionBoot, subscribeToSession } from './session';

/**
 * Everything the application needs, wrapped around everything it renders.
 *
 * Order matters. The error boundary is outermost so a failure anywhere below it
 * -- including in the router -- still produces a page with a sentence on it
 * rather than a white screen. `SessionBoot` is innermost, directly around the
 * router: it holds the first render until the silent renewal has answered, and
 * everything above it has to be mounted for that answer to be usable.
 */
function SessionCacheReset({ children }: { children: React.ReactNode }) {
  const queryClient = useMemo(() => createQueryClient(), []);

  useEffect(() => {
    let hadSession = getSessionState().session !== null;
    return subscribeToSession(() => {
      const hasSession = getSessionState().session !== null;
      // Anything read during a session belongs to that session. Emptying the
      // cache as it ends is what stops the next person to sign in on this
      // machine from seeing a frame of the previous one's data.
      if (hadSession && !hasSession) {
        queryClient.clear();
      }
      hadSession = hasSession;
    });
  }, [queryClient]);

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}

/**
 * Everything except the router.
 *
 * Split out so tests can drive the same providers with a `MemoryRouter` and a
 * chosen starting URL, rather than testing a different tree from the one that
 * ships.
 */
export function AppProviders({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary where="app">
      <SessionCacheReset>
        <NotificationProvider>
          <SessionBoot>
            <HiddenColumnsProvider>{children}</HiddenColumnsProvider>
          </SessionBoot>
        </NotificationProvider>
      </SessionCacheReset>
    </ErrorBoundary>
  );
}

export function App() {
  return (
    <AppProviders>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AppProviders>
  );
}
